import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';

export interface AdsDailyLast30DaysDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class AdsDailyLast30DaysDataLoaderFactory extends Factory<
    DataLoader,
    AdsDailyLast30DaysDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsDailyLast30DaysDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: AdsDailyLast30DaysDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap;

                // filter out from context REVENUE_PAYER
                const datasourceColumns = queryContext.datasourceColumns;
                // const datasourceColumns = queryContext.datasourceColumns.filter(
                //     (dsCol) => dsCol.id !== 'REVENUE_PAYER'
                // );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where } =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,

                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [`date >= date_dim.days_ago_30`],
                        }
                    );

                const tableFullPath = generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH date_dim AS (
    SELECT *
      , DATE_SUB(current_d, INTERVAL 30 DAY) AS days_ago_30
    FROM 
    (
      SELECT MAX(date) AS current_d FROM \`${tableFullPath}\`
    )
  )
  
  SELECT 
    ${splitSelect}date
    , SUM(ads_kpis.impressions) AS impressions
    , SAFE_DIVIDE(SUM(ads_kpis.engaged_users), SUM(dau)) AS engagement_rate
    -- ads impressions per DEU
    , SAFE_DIVIDE(SUM(ads_kpis.impressions), SUM(ads_kpis.engaged_users)) AS impressions_per_deu
  FROM \`${tableFullPath}\`,
    date_dim
  ${where}
  GROUP BY ${splitGroupBy}date
  ORDER BY ${splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
